import { Observer as RootObserver } from "@syncfusion/ej2-base";
// node_modules holds several copies of ej2-base — each nested package pins its own
// version and webpack never dedupes across versions — and every copy has its own
// Observer class. A guard on the root copy alone leaves the PivotView, the grids
// and the navigations widgets (sidebar, toolbar, context menu) unprotected, which
// is exactly the gap the old PivotChart-scoped patch had. syncfusionObserverGuard
// .copies.test.js fails the build when the set of copies drifts after an install.
import { Observer as PivotViewObserver } from "@syncfusion/ej2-pivotview/node_modules/@syncfusion/ej2-base";
import { Observer as ReactPivotViewObserver } from "@syncfusion/ej2-react-pivotview/node_modules/@syncfusion/ej2-base";
import { Observer as ReactGridsObserver } from "@syncfusion/ej2-react-grids/node_modules/@syncfusion/ej2-base";
import { Observer as ReactNavigationsObserver } from "@syncfusion/ej2-react-navigations/node_modules/@syncfusion/ej2-base";
import { ComponentBase as RootComponentBase } from "@syncfusion/ej2-react-base";
import { ComponentBase as ReactPivotViewComponentBase } from "@syncfusion/ej2-react-pivotview/node_modules/@syncfusion/ej2-react-base";
import { ComponentBase as ReactGridsComponentBase } from "@syncfusion/ej2-react-grids/node_modules/@syncfusion/ej2-react-base";
import { ComponentBase as ReactNavigationsComponentBase } from "@syncfusion/ej2-react-navigations/node_modules/@syncfusion/ej2-react-base";

// Syncfusion's Observer.destroy() sets boundedEvents = undefined, and every
// entry point — on(), off(), notify() — reaches notExist(), which dereferences
// this.boundedEvents.hasOwnProperty and throws. A widget that is destroyed
// while its own async work is still queued (a dataBind deferred by
// Base.saveChanges, a DataManager query resolving on a microtask, a module
// re-injected on a destroyed parent) then takes the whole React tree down
// with an uncaught TypeError: the "first project after login" and the
// "page crashes after project + scenario creation" white screens.
//
// This guard used to live as a module-scope IIFE in PivotChartComponent.js
// and covered on() only. That chunk is lazily loaded, so a user whose first
// action after login never rendered a chart had no guard at all — which is
// why the crash was "only the first time" and never reproduced locally, where
// the chunk loads too fast to lose the race. Installing it at app boot
// (src/index.js), on every Observer class in the bundle, and covering the
// three entry points removes the race wherever the late callback comes from.
// The app-side ordering fix (useDialogSafeNavigate) removes the most common
// trigger; this is the backstop for the ones it cannot see.
//
// Transparent on a live observer: nothing is caught, nothing is swallowed —
// a handler that throws still throws.

export const OBSERVER_CLASSES = [
  RootObserver,
  PivotViewObserver,
  ReactPivotViewObserver,
  ReactGridsObserver,
  ReactNavigationsObserver,
];

const guarded = new WeakSet();

export function guardObserverClass(ObserverClass) {
  const proto = ObserverClass.prototype;
  if (guarded.has(proto)) return;
  guarded.add(proto);

  const originalOn = proto.on;
  proto.on = function (property, handler, context, id) {
    if (!this.boundedEvents) return;
    return originalOn.call(this, property, handler, context, id);
  };

  const originalOff = proto.off;
  proto.off = function (property, handler, id) {
    if (!this.boundedEvents) return;
    return originalOff.call(this, property, handler, id);
  };

  // notify() on a destroyed observer behaves like notify() on an unknown
  // event: nothing to call, but the success callback still runs — that is
  // the contract the original code has for a missing property.
  const originalNotify = proto.notify;
  proto.notify = function (property, argument, successHandler, errorHandler) {
    if (!this.boundedEvents) {
      if (successHandler) successHandler.call(this, argument);
      return;
    }
    return originalNotify.call(this, property, argument, successHandler, errorHandler);
  };
}

// The React wrapper (ej2-react-base ComponentBase) pushes prop changes into
// the widget through refreshProperties → setProperties. It runs synchronously
// from componentDidUpdate and, for accordion / tab / splitter, from a
// setTimeout queued in componentWillReceiveProps. Neither path checks whether
// the widget has been destroyed in the meantime — a route rewrite that
// remounts the schedule view destroys the Gantt page's SplitterComponent while
// its deferred refresh is still queued, and setProperties({ resizeStart })
// then walks removeEventListener → Observer.off on a dead observer. Reproduced
// deterministically on /process/view/gantt/<id>/undefined/undefined (6/6
// loads on dev, 0/6 with the guards). The synchronous variant of the same
// call throws inside React's commit phase and lands on the ErrorBoundary —
// the hippo page. Applying props to a destroyed widget has no observable
// effect anyway (its DOM is gone), so skipping the refresh is the whole fix.
export const COMPONENT_BASE_CLASSES = [
  RootComponentBase,
  ReactPivotViewComponentBase,
  ReactGridsComponentBase,
  ReactNavigationsComponentBase,
];

export function guardComponentBaseClass(ComponentBaseClass) {
  const proto = ComponentBaseClass.prototype;
  if (guarded.has(proto)) return;
  guarded.add(proto);

  const originalRefreshProperties = proto.refreshProperties;
  proto.refreshProperties = function (dProps, nextProps, silent) {
    if (this.isDestroyed) return;
    return originalRefreshProperties.call(this, dProps, nextProps, silent);
  };
}

export function installSyncfusionObserverGuard() {
  OBSERVER_CLASSES.forEach(guardObserverClass);
  COMPONENT_BASE_CLASSES.forEach(guardComponentBaseClass);
}
